import { useState, useCallback } from 'react';
import { getAdviserDetails } from '../api/contract_templates';

/**
 * Custom hook for fetching and managing adviser details.
 * 
 * @returns {Object} Object containing:
 *   - adviserDetails: The fetched adviser details object or null
 *   - adviserDetailsLoading: Boolean indicating if a request is in progress
 *   - adviserDetailsError: Error message string or null
 *   - fetchAdviserDetails: Function to fetch adviser details (adviserId, clientId)
 *   - resetAdviserDetails: Function to reset all state to initial values
 */
export const useAdviserDetails = () => {
  const [adviserDetails, setAdviserDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetches adviser details for the given adviser and client IDs.
   * 
   * @param {string} adviserId - The ID of the adviser to fetch details for
   * @param {string} clientId - The ID of the client (required for access control)
   */
  const fetchAdviserDetails = useCallback(async (adviserId, clientId) => {
    if (!adviserId || !clientId) {
      setAdviserDetails(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const details = await getAdviserDetails(adviserId, clientId);
      setAdviserDetails(details);
    } catch (error) {
      console.error('Error fetching adviser details:', error);
      setError('Failed to load adviser details');
      setAdviserDetails(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const resetAdviserDetails = useCallback(() => {
    setAdviserDetails(null);
    setLoading(false);
    setError(null);
  }, []);

  return {
    adviserDetails,
    adviserDetailsLoading: loading,
    adviserDetailsError: error,
    fetchAdviserDetails,
    resetAdviserDetails,
  };
}; 